/**
 * Exports DTS officiels (CNSS, CNAMGS) — reproduction à l'identique des gabarits fournis.
 *
 * Principe : le gabarit officiel épuré (`src/assets/dts-gabarits/`) est CHARGÉ, jamais reconstruit.
 * L'export n'écrit que les cellules de saisie ; fusions, libellés, mise en page, images et
 * FORMULES du gabarit sont conservés (totaux, cotisations, effectif, Avis liés à la feuille DTS).
 * Les valeurs mises en cache des formules sont renseignées pour les lecteurs qui ne recalculent
 * pas ; Excel et LibreOffice recalculent de toute façon à l'ouverture (`fullCalcOnLoad`).
 *
 * Les taux viennent des paramètres de paie du moteur (`DtsOfficiel`), jamais écrits en dur ici.
 *
 * Une seule extension du gabarit : pour plus d'un salarié (CNSS), des blocs de lignes sont insérés
 * AVANT la ligne « SOUS TOTAL » comme le ferait l'utilisateur dans Excel ; les plages de SUM sont
 * étendues à toutes les lignes salariés et le sous-total est reporté d'une page à l'autre.
 */
import type { Cell, Row, Style, Workbook, Worksheet } from 'exceljs';
import { chargerExcelJs, versArrayBuffer } from './ExcelJsLoader';
import { chargerGabaritDts, type TypeGabaritDts } from './DtsGabarits';
import type {
  DtsMoisOfficiel,
  DtsOfficielAgrege,
  DtsSalarieOfficiel,
} from '@domain/fiscalite/DtsOfficiel';
import { DtsOfficiel } from '@domain/fiscalite/DtsOfficiel';

type Valeur = string | number | Date | null | undefined;

/** Nombre de salariés par page imprimée (CNSS) avant un « sous-total à reporter ». */
export const SALARIES_PAR_PAGE_CNSS = 20;

/* ------------------------------------------------------------------------------------------ */
/* Cellules de saisie : tout le reste du gabarit doit rester identique (voir les tests).        */
/* ------------------------------------------------------------------------------------------ */

/** CNAMGS (feuille « Feuil1 ») : cellules de saisie hors lignes salariés (à partir de la ligne 40). */
export const CELLULES_SAISIE_CNAMGS = [
  'E8',
  'A10',
  'A12',
  'B14',
  'C14',
  'B16',
  'D16',
  'A18',
  'F13',
  'F14',
  'F15',
  'F18',
  'F28',
  'H28',
  'J28',
  'E30',
] as const;

/** CNSS, feuille « DTS » : en-tête (au-dessus de la liste des salariés). */
export const CELLULES_SAISIE_CNSS_ENTETE = [
  'A11',
  'E11',
  'F11',
  'G11',
  'I11',
  'A13',
  'B15',
  'D15',
  'B17',
  'D17',
  'A19',
  'E21',
  'I18',
] as const;

/** CNSS, feuille « DTS » : cellules de la zone RECAP (taux) — adresses du gabarit à un salarié. */
export const CELLULES_SAISIE_CNSS_RECAP = ['D32'] as const;

/** CNSS, feuille « Avis ». */
export const CELLULES_SAISIE_AVIS = [
  'F7',
  'F9',
  'D10',
  'D11',
  'C12',
  'C13',
  'C14',
  'D21',
  'D26',
  'D27',
  'D31',
  'D32',
] as const;

/** Colonnes (plafonné CNSS, déplafonné, jours) des trois mois, feuille « DTS » CNSS. */
const COLONNES_MOIS_CNSS = [
  ['H', 'I', 'J'],
  ['K', 'L', 'M'],
  ['N', 'O', 'P'],
] as const;

/** Colonnes (assiette, jours) des trois mois, gabarit CNAMGS. */
const COLONNES_MOIS_CNAMGS = [
  ['F', 'G'],
  ['H', 'I'],
  ['J', 'K'],
] as const;

/** Colonnes des sous-totaux et totaux (plafonné + déplafonné) de la feuille DTS CNSS. */
const COLONNES_SOMMES_CNSS = ['H', 'I', 'K', 'L', 'N', 'O'] as const;

/** Taux CNSS inscrit en dur dans la formule du gabarit (B23 = B21*23%). */
const TAUX_FORMULE_GABARIT_CNSS = 0.23;

/* ------------------------------------------------------------------------------------------ */
/* Aides ExcelJS                                                                                */
/* ------------------------------------------------------------------------------------------ */

function estEsclave(cell: Cell): boolean {
  return cell.isMerged && cell.master.address !== cell.address;
}

function fusionne(ws: Worksheet, plage: string): void {
  ws.mergeCells(plage);
}

function ecrire(ws: Worksheet, adresse: string, valeur: Valeur, numFmt?: string): void {
  if (valeur === null || valeur === undefined || valeur === '') return;
  const cell = ws.getCell(adresse);
  if (estEsclave(cell)) {
    throw new Error(`Cellule ${adresse} fusionnée avec ${cell.master.address} : écriture impossible`);
  }
  cell.value = valeur;
  if (numFmt) cell.numFmt = numFmt;
}

/** Conserve la formule du gabarit et renseigne sa valeur calculée (cache). */
function renseignerResultat(ws: Worksheet, adresse: string, resultat: number): void {
  const cell = ws.getCell(adresse);
  const v = cell.value;
  if (v === null || typeof v !== 'object' || !('formula' in v) || typeof v.formula !== 'string') {
    throw new Error(`Le gabarit n'a pas de formule en ${adresse}`);
  }
  cell.value = { formula: v.formula, result: resultat };
}

function poserFormule(ws: Worksheet, adresse: string, formule: string, resultat: number): void {
  ws.getCell(adresse).value = { formula: formule, result: resultat };
}

function dateUtc(iso: string): Date | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(iso);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1])));
}

function clonerStyle(style: Partial<Style>): Partial<Style> {
  return JSON.parse(JSON.stringify(style)) as Partial<Style>;
}

/** Hauteur de ligne ; `undefined` retire la hauteur personnalisée (le type ExcelJS n'accepte que number). */
function fixerHauteur(ligne: Row, hauteur: number | undefined): void {
  Reflect.set(ligne, 'height', hauteur);
}

function somme(valeurs: ReadonlyArray<number | null | undefined>): number {
  return valeurs.reduce<number>((s, v) => s + (v ?? 0), 0);
}

async function ouvrirGabarit(type: TypeGabaritDts): Promise<Workbook> {
  const { Workbook: Classeur } = await chargerExcelJs();
  const wb = new Classeur();
  await wb.xlsx.load(await chargerGabaritDts(type));
  wb.creator = 'Nexxus ERP';
  wb.lastModifiedBy = 'Nexxus ERP';
  wb.modified = new Date();
  wb.calcProperties.fullCalcOnLoad = true;
  return wb;
}

function feuille(wb: Workbook, nom: string): Worksheet {
  const ws = wb.getWorksheet(nom);
  if (!ws) throw new Error(`Gabarit DTS invalide : feuille « ${nom} » absente`);
  return ws;
}

async function enBuffer(wb: Workbook): Promise<ArrayBuffer> {
  return versArrayBuffer(await wb.xlsx.writeBuffer());
}

function masseMois(salaries: readonly DtsSalarieOfficiel[], i: 0 | 1 | 2, champ: keyof DtsMoisOfficiel): number {
  return somme(salaries.map((s) => s.mois[i][champ] as number | null));
}

/* ------------------------------------------------------------------------------------------ */
/* Export                                                                                       */
/* ------------------------------------------------------------------------------------------ */

interface InstantaneLigne {
  hauteur: number | undefined;
  cellules: Array<{ style: Partial<Style>; valeur: Cell['value']; esclave: boolean }>;
}

export class DtsOfficielExcelExporter {
  static async classeurCnss(agrege: DtsOfficielAgrege): Promise<ArrayBuffer> {
    const wb = await ouvrirGabarit('cnss');
    this.feuilleCnss(feuille(wb, 'DTS'), feuille(wb, 'Avis'), agrege);
    return enBuffer(wb);
  }

  static async classeurCnamgs(agrege: DtsOfficielAgrege): Promise<ArrayBuffer> {
    const wb = await ouvrirGabarit('cnamgs');
    this.feuilleCnamgs(wb.worksheets[0] ?? feuille(wb, 'Feuil1'), agrege);
    return enBuffer(wb);
  }

  static nomFichierCnss(agrege: DtsOfficielAgrege): string {
    return DtsOfficiel.nomFichierCnss(agrege);
  }

  static nomFichierCnamgs(agrege: DtsOfficielAgrege): string {
    return DtsOfficiel.nomFichierCnamgs(agrege);
  }

  /* ---------------------------------- CNAMGS ------------------------------------------------ */

  private static feuilleCnamgs(ws: Worksheet, agrege: DtsOfficielAgrege): void {
    const e = agrege.employeur;
    const periode = `Période : T${agrege.numero_trimestre}/${agrege.annee}`;
    ecrire(ws, 'E8', agrege.reference_depot ? `${periode} : ${agrege.reference_depot}` : periode);
    ecrire(ws, 'A10', e.matricule_cnamgs);
    ecrire(ws, 'A12', e.raison_sociale);
    ecrire(ws, 'B14', e.bp);
    ecrire(ws, 'C14', `VILLE : ${e.ville}`);
    ecrire(ws, 'B16', e.telephone);
    ecrire(ws, 'D16', e.fax);
    // « Email : » est un libellé fusionné A18:C18 à compléter (pas de cellule de saisie séparée).
    if (e.email) ecrire(ws, 'A18', ` Email : ${e.email}`);

    ecrire(ws, 'F13', agrege.taux_cnamgs);
    ecrire(ws, 'F14', agrege.taux_cnamgs_patronal);
    ecrire(ws, 'F15', agrege.taux_cnamgs_salarial);
    ecrire(ws, 'F18', agrege.plafond_cnamgs);
    ecrire(ws, 'F28', agrege.libelles_mois[0]);
    ecrire(ws, 'H28', agrege.libelles_mois[1]);
    ecrire(ws, 'J28', agrege.libelles_mois[2]);
    ecrire(ws, 'E30', agrege.date_limite_depot_long);

    // Lignes salariés : à partir de la ligne 40, mise en forme de la ligne 40 du gabarit.
    const premiere = 40;
    const modele = ws.getRow(premiere);
    const stylesModele: Array<Partial<Style>> = [];
    for (let c = 1; c <= 11; c += 1) stylesModele.push(clonerStyle(modele.getCell(c).style));
    const hauteur = modele.height;

    agrege.salaries.forEach((salarie, i) => {
      const r = premiere + i;
      const ligne = ws.getRow(r);
      if (i > 0) {
        stylesModele.forEach((st, idx) => {
          ligne.getCell(idx + 1).style = clonerStyle(st);
        });
        if (hauteur !== undefined) ligne.height = hauteur;
      }
      ecrire(ws, `A${r}`, salarie.numero);
      ecrire(ws, `B${r}`, salarie.numero_cnamgs || salarie.numero_cnss);
      ecrire(ws, `C${r}`, salarie.nom_prenom);
      ecrire(ws, `D${r}`, salarie.embauche);
      ecrire(ws, `E${r}`, salarie.cessation);
      salarie.mois.forEach((mois, idx) => {
        const [assiette, jours] = COLONNES_MOIS_CNAMGS[idx]!;
        ecrire(ws, `${assiette}${r}`, mois.assiette_cnamgs, '#,##0');
        ecrire(ws, `${jours}${r}`, mois.jours);
      });
    });

    // Impression : en-tête du tableau répété sur chaque page (les totaux du gabarit couvrent F40:K5000).
    ws.pageSetup.printTitlesRow = '38:39';

    // Valeurs en cache des formules du gabarit (inchangées).
    const m = [0, 1, 2].map((i) => masseMois(agrege.salaries, i as 0 | 1 | 2, 'assiette_cnamgs'));
    const masse = somme(m);
    const jtotal = masse * agrege.taux_cnamgs;
    renseignerResultat(ws, 'C24', agrege.salaries.length);
    renseignerResultat(ws, 'F27', m[0]!);
    renseignerResultat(ws, 'H27', m[1]!);
    renseignerResultat(ws, 'J27', m[2]!);
    renseignerResultat(ws, 'G24', masse);
    renseignerResultat(ws, 'J24', jtotal);
    renseignerResultat(ws, 'J25', masse * agrege.taux_cnamgs_patronal);
    renseignerResultat(ws, 'J26', masse * agrege.taux_cnamgs_salarial);
    renseignerResultat(ws, 'B20', jtotal);
  }

  /* ----------------------------------- CNSS ------------------------------------------------- */

  private static feuilleCnss(ws: Worksheet, avis: Worksheet, agrege: DtsOfficielAgrege): void {
    const e = agrege.employeur;
    const salaries = agrege.salaries;
    const n = salaries.length;

    // --- En-tête (cellules de saisie, au-dessus de la liste) ---
    ecrire(ws, 'A11', e.matricule_cnss);
    ecrire(ws, 'E11', agrege.numero_trimestre);
    ecrire(ws, 'F11', agrege.annee);
    ecrire(ws, 'G11', agrege.regime);
    ecrire(ws, 'I11', e.sigle);
    ecrire(ws, 'A13', e.raison_sociale);
    ecrire(ws, 'B15', e.bp);
    ecrire(ws, 'D15', e.ville);
    ecrire(ws, 'B17', e.telephone);
    ecrire(ws, 'D17', e.fax);
    // « Email : » est un libellé fusionné A19:D19 à compléter (pas de cellule de saisie séparée).
    if (e.email) ecrire(ws, 'A19', ` Email : ${e.email}`);

    // --- Liste des salariés : blocs de 2 lignes (CNSS + sous-ligne du gabarit laissée vide) ---
    const mise = this.disposerSalaries(ws, n);
    const taux100 = Math.round(agrege.taux_cnss * 100 * 1e6) / 1e6;
    salaries.forEach((salarie, i) => {
      const r = mise.lignesSalaries[i]!;
      ecrire(ws, `A${r}`, salarie.numero);
      ecrire(ws, `B${r}`, salarie.numero_cnss || salarie.numero_cnamgs);
      ecrire(ws, `C${r}`, salarie.numero_paie);
      ecrire(ws, `D${r}`, salarie.nom_prenom);
      ecrire(ws, `E${r}`, taux100);
      const emb = dateUtc(salarie.embauche);
      if (emb) ecrire(ws, `F${r}`, emb, 'dd/mm/yyyy');
      const ces = dateUtc(salarie.cessation);
      if (ces) ecrire(ws, `G${r}`, ces, 'dd/mm/yyyy');
      salarie.mois.forEach((mois, idx) => {
        const [plaf, deplaf, jours] = COLONNES_MOIS_CNSS[idx]!;
        ecrire(ws, `${plaf}${r}`, mois.salaire_plafonne_cnss, '#,##0');
        ecrire(ws, `${deplaf}${r}`, mois.salaire_deplafonne, '#,##0');
        ecrire(ws, `${jours}${r}`, mois.jours);
      });
    });

    // --- Sommes : plages de SUM sur TOUTES les lignes salariés ---
    const plage = (col: string): string =>
      mise.pages.map((p) => `${col}${p.debut}:${col}${p.fin}`).join(',');
    const cumul = (col: string, jusquaPage: number): string =>
      mise.pages
        .slice(0, jusquaPage + 1)
        .map((p) => `${col}${p.debut}:${col}${p.fin}`)
        .join(',');
    const champParColonne: Record<string, [0 | 1 | 2, keyof DtsMoisOfficiel]> = {
      H: [0, 'salaire_plafonne_cnss'],
      I: [0, 'salaire_deplafonne'],
      K: [1, 'salaire_plafonne_cnss'],
      L: [1, 'salaire_deplafonne'],
      N: [2, 'salaire_plafonne_cnss'],
      O: [2, 'salaire_deplafonne'],
    };
    const totalColonne = (col: string, nbSalaries: number): number => {
      const [mois, champ] = champParColonne[col]!;
      return somme(salaries.slice(0, nbSalaries).map((s) => s.mois[mois][champ] as number | null));
    };

    // Sous-totaux à reporter (pages intermédiaires) : cumul des salariés des pages déjà imprimées.
    mise.sousTotaux.forEach((ligne, p) => {
      const jusqua = mise.pages.slice(0, p + 1).reduce((s, pg) => s + pg.nombre, 0);
      for (const col of COLONNES_SOMMES_CNSS) {
        poserFormule(ws, `${col}${ligne}`, `SUM(${cumul(col, p)})`, totalColonne(col, jusqua));
      }
    });

    const rr = mise.recap; // ligne « RECAP CNSS » (32 dans le gabarit à un salarié)
    const masseMoisCnss = (['H', 'K', 'N'] as const).map((c) => totalColonne(c, n));
    for (const col of ['H', 'K', 'N'] as const) {
      poserFormule(ws, `${col}${rr}`, `SUM(${plage(col)})`, totalColonne(col, n));
    }
    ecrire(ws, `D${rr}`, agrege.taux_cnss);

    // Formules du gabarit conservées (adresses décalées si des lignes ont été insérées).
    const masse = somme(masseMoisCnss);
    poserFormule(ws, 'B21', `H${rr}+K${rr}+N${rr}`, masse);
    const tauxGabaritIdentique = Math.abs(agrege.taux_cnss - TAUX_FORMULE_GABARIT_CNSS) < 1e-9;
    const cotBrute = masse * agrege.taux_cnss;
    if (tauxGabaritIdentique) {
      renseignerResultat(ws, 'B23', cotBrute);
    } else {
      // Le taux du moteur diffère du 23 % figé dans la formule du gabarit : on lie B23 à la cellule de taux.
      poserFormule(ws, 'B23', `B21*D${rr}`, cotBrute);
    }
    const deduction = agrege.deduction_af;
    let cotNette = cotBrute;
    if (deduction > 0) {
      // DEC-15 options B/C : E21 reprend le total déduit de l'Avis ; E23 = cotisation brute − déduction.
      poserFormule(ws, 'E21', 'Avis!D28', deduction);
      cotNette = cotBrute - deduction;
      poserFormule(ws, 'E23', 'B23-E21', cotNette);
    } else {
      renseignerResultat(ws, 'E23', cotNette);
    }
    renseignerResultat(ws, `O${mise.globale}`, cotNette);

    // Effectif total : nombre de noms de la liste.
    poserFormule(ws, 'I18', `COUNTA(${plage('D')})`, n);

    // --- Impression ---
    ws.pageSetup.printTitlesRow = '25:26';
    ws.pageSetup.fitToPage = true;
    ws.pageSetup.fitToWidth = 1;
    ws.pageSetup.fitToHeight = 0;
    mise.sousTotaux.forEach((ligne) => ws.getRow(ligne).addPageBreak());

    this.feuilleAvis(avis, agrege, cotBrute, cotNette, masse);
  }

  /**
   * Insère les blocs de lignes salariés avant la ligne « SOUS TOTAL » du gabarit (équivalent de
   * « Insérer des lignes » dans Excel), avec un sous-total à reporter à chaque fin de page.
   * Pour un seul salarié la feuille reste strictement celle du gabarit.
   */
  private static disposerSalaries(
    ws: Worksheet,
    n: number,
  ): {
    lignesSalaries: number[];
    pages: Array<{ debut: number; fin: number; nombre: number }>;
    sousTotaux: number[];
    recap: number;
    globale: number;
  } {
    const PREMIERE = 27;
    const BLOC = [27, 28] as const;
    // espace, sous-total, espace, RECAP x3, puis les lignes de fin du gabarit (cellules sans contenu)
    const QUEUE = Array.from({ length: Math.max(34, ws.rowCount) - 28 }, (_v, i) => 29 + i);
    const LARGEUR = 16;

    const instantane = (r: number): InstantaneLigne => {
      const ligne: Row = ws.getRow(r);
      const cellules: InstantaneLigne['cellules'] = [];
      for (let c = 1; c <= LARGEUR; c += 1) {
        const cell = ligne.getCell(c);
        cellules.push({
          style: clonerStyle(cell.style),
          valeur: estEsclave(cell) ? null : cell.value,
          esclave: estEsclave(cell),
        });
      }
      return { hauteur: ligne.height, cellules };
    };

    const bloc = BLOC.map(instantane);
    const queue = QUEUE.map(instantane);

    // Fusions du gabarit sous la première ligne salarié : retirées puis recréées à leur nouvelle place.
    const fusionsBloc = ['A27:A28', 'D27:D28', 'I27:I28', 'L27:L28', 'O27:O28'];
    const fusionsQueue = [
      'A30:D30',
      'A32:A33',
      'C32:C33',
      'E32:G32',
      'I32:J32',
      'E33:G33',
      'I33:J33',
      'A34:N34',
      'O34:P34',
    ];
    [...fusionsBloc, ...fusionsQueue].forEach((f) => ws.unMergeCells(f));
    for (let r = 27; r <= QUEUE[QUEUE.length - 1]!; r += 1) {
      const ligne = ws.getRow(r);
      for (let c = 1; c <= LARGEUR; c += 1) {
        const cell = ligne.getCell(c);
        cell.value = null;
        cell.style = {};
      }
      fixerHauteur(ligne, undefined);
    }

    const poser = (inst: InstantaneLigne, cible: number): void => {
      const ligne = ws.getRow(cible);
      inst.cellules.forEach((cellule, idx) => {
        const cell = ligne.getCell(idx + 1);
        cell.style = clonerStyle(cellule.style);
        if (!cellule.esclave && cellule.valeur !== null) cell.value = cellule.valeur;
      });
      fixerHauteur(ligne, inst.hauteur);
    };
    const decaler = (plageA1: string, delta: number): string =>
      plageA1.replace(/([A-Z]+)(\d+)/g, (_m, col: string, lig: string) => `${col}${Number(lig) + delta}`);

    const nbSalaries = Math.max(n, 1);
    const nbPages = Math.ceil(nbSalaries / SALARIES_PAR_PAGE_CNSS);
    const lignesSalaries: number[] = [];
    const pages: Array<{ debut: number; fin: number; nombre: number }> = [];
    const sousTotaux: number[] = [];
    let curseur = PREMIERE;
    let restants = nbSalaries;
    for (let p = 0; p < nbPages; p += 1) {
      const dansPage = Math.min(SALARIES_PAR_PAGE_CNSS, restants);
      const debut = curseur;
      for (let k = 0; k < dansPage; k += 1) {
        BLOC.forEach((_r, idx) => poser(bloc[idx]!, curseur + idx));
        fusionsBloc.forEach((f) => fusionne(ws, decaler(f, curseur - PREMIERE)));
        // fusionner réapplique le style du maître : on remet celui du gabarit cellule par cellule.
        BLOC.forEach((_r, idx) => poser(bloc[idx]!, curseur + idx));
        lignesSalaries.push(curseur);
        curseur += 2;
      }
      pages.push({ debut, fin: curseur - 1, nombre: dansPage });
      restants -= dansPage;
      if (p < nbPages - 1) {
        // fin de page intermédiaire : espace + « SOUS TOTAL A REPORTER PAGE SUIVANTE »
        poser(queue[0]!, curseur);
        poser(queue[1]!, curseur + 1);
        fusionne(ws, decaler('A30:D30', curseur - 29));
        poser(queue[1]!, curseur + 1);
        sousTotaux.push(curseur + 1);
        curseur += 2;
      }
    }
    // queue du gabarit : espace, sous-total (dernière page, laissé vide), espace, RECAP, cotisation globale
    const decalageQueue = curseur - 29;
    queue.forEach((inst, idx) => poser(inst, curseur + idx));
    fusionsQueue.forEach((f) => fusionne(ws, decaler(f, decalageQueue)));
    queue.forEach((inst, idx) => poser(inst, curseur + idx));

    return {
      lignesSalaries,
      pages,
      sousTotaux,
      recap: 32 + decalageQueue,
      globale: 34 + decalageQueue,
    };
  }

  private static feuilleAvis(
    ws: Worksheet,
    agrege: DtsOfficielAgrege,
    cotBrute: number,
    cotNette: number,
    masse: number,
  ): void {
    const e = agrege.employeur;
    ecrire(ws, 'F7', e.matricule_cnss);
    ecrire(ws, 'F9', e.raison_sociale);
    ecrire(ws, 'D10', `N°CNSS: ${e.matricule_cnss}`);
    ecrire(ws, 'D11', `BP: ${e.bp}   Ville: ${e.ville}`);
    ecrire(ws, 'C12', e.sigle);
    ecrire(ws, 'C13', e.nif);
    ecrire(ws, 'C14', e.rccm);
    ecrire(ws, 'D21', `${agrege.numero_trimestre}T${agrege.annee}`);

    // DEC-15 : D26 allocations familiales, D27 ristourne (0 par défaut, libellé conservé).
    ws.getCell('D26').value = agrege.allocations_familiales;
    ws.getCell('D27').value = agrege.ristourne_af;
    // CNAMGS : même formule que le gabarit CNAMGS (masse × taux).
    const masseCnamgs = agrege.masse_cnamgs;
    ws.getCell('D31').value = masseCnamgs;
    ws.getCell('D32').value = masseCnamgs * agrege.taux_cnamgs;

    // Valeurs en cache des formules du gabarit.
    renseignerResultat(ws, 'D24', masse);
    renseignerResultat(ws, 'D25', cotBrute);
    renseignerResultat(ws, 'D28', agrege.deduction_af);
    renseignerResultat(ws, 'D30', cotBrute - agrege.deduction_af - 0);
    renseignerResultat(ws, 'D33', cotNette + masseCnamgs * agrege.taux_cnamgs);
  }
}
